import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LandingPage.css';

const GALLERY = [
  { src: '/whot-gallery/whot-table.webp', alt: 'Whot game table', label: 'The Whot table' },
  { src: '/whot-gallery/whot-hand.webp', alt: 'Cards in a player hand', label: 'Your hand' },
  { src: '/whot-gallery/whot-your-turn.webp', alt: 'Whot game showing it is your turn', label: 'Your turn' },
  { src: '/whot-gallery/whot-match.webp', alt: 'Whot match in progress', label: 'Live match' },
];

function formatNaira(value) {
  return `₦${Number(value || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
}

function LobbyPreview() {
  const [tiers, setTiers] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const navigate = useNavigate();

  const refresh = useCallback(async (signal) => {
    try {
      const response = await fetch('/api/stakes/public/lobby', { method: 'GET', credentials: 'omit', cache: 'no-store', signal });
      if (!response.ok) throw new Error('Lobby unavailable');
      const payload = await response.json();
      if (!Array.isArray(payload.tiers)) throw new Error('Invalid lobby response');
      setTiers(payload.tiers);
      setUpdatedAt(payload.updatedAt || null);
      setUnavailable(false);
    } catch (error) {
      if (error.name !== 'AbortError') setUnavailable(true);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    const timer = window.setInterval(() => refresh(controller.signal), 5000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [refresh]);

  return (
    <section className="jw-landing-section" aria-labelledby="jw-lobby-title">
      <div className="jw-landing-section-heading">
        <div><p className="jw-landing-eyebrow">PLAY IS HAPPENING</p><h2 id="jw-lobby-title">Live lobby</h2></div>
        <span className="jw-live-pill"><i aria-hidden="true" /> LIVE</span>
      </div>
      <p className="jw-landing-muted">See the current entry stake, prize pool and players waiting. Counts refresh every few seconds.</p>
      {tiers.length > 0 ? (
        <div className="jw-lobby-grid">
          {tiers.map((tier, index) => (
            <button className="jw-lobby-card" key={`${tier.tier}-${index}`} type="button" onClick={() => navigate('/register')} aria-label={`Join ${tier.tier}; stake ${formatNaira(tier.stake)}`}>
              <span className="jw-lobby-tier">{tier.tier}</span>
              <span className="jw-lobby-value">{formatNaira(tier.stake)}</span><span className="jw-lobby-caption">Entry stake</span>
              <span className="jw-lobby-prize">{formatNaira(tier.prize)}</span><span className="jw-lobby-caption">Prize pool</span>
              <span className="jw-lobby-waiting">{Number(tier.waiting || 0)} waiting</span>
              <span className="jw-lobby-cta">Play now →</span>
            </button>
          ))}
        </div>
      ) : <div className="jw-lobby-empty" role="status">{unavailable ? 'The live lobby is temporarily unavailable. Please try again shortly.' : 'Loading live stake tiers…'}</div>}
      {updatedAt && <p className="jw-lobby-updated">Updated {new Date(updatedAt).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>}
    </section>
  );
}

function GalleryCard({ item }) {
  const [failed, setFailed] = useState(false);
  return <figure className="jw-gallery-card">
    {!failed && <img src={item.src} alt={item.alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />}
    {failed && <div className="jw-gallery-placeholder" aria-label={`${item.label} screenshot will appear here`}><span className="jw-gallery-placeholder-mark">9<span>W</span></span><span>Gameplay screenshot coming soon</span></div>}
    <figcaption>{item.label}</figcaption>
  </figure>;
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <main className="jw-landing">
      <header className="jw-landing-header">
        <Link to="/" className="jw-landing-brand" aria-label="9jaWin home"><span>9ja</span><strong>Win</strong></Link>
        <span className="jw-landing-whot">WHOT · NIGERIA</span>
      </header>
      <section className="jw-landing-hero">
        <div className="jw-landing-hero-glow" aria-hidden="true" />
        <span className="jw-landing-badge">YOUR NEXT WHOT MATCH STARTS HERE</span>
        <h1>Play Whot.<br /><span>Make your move.</span></h1>
        <p>Challenge real players in stake-based Whot matches. Choose your table, play your cards and see how the match unfolds.</p>
        <div className="jw-landing-actions">
          <Link className="jw-landing-button jw-landing-button-primary" to="/register">Get Started</Link>
          <Link className="jw-landing-button jw-landing-button-secondary" to="/login">I have an account</Link>
        </div>
        <p className="jw-landing-no-guarantee">Skill, strategy and match outcomes vary. Winnings are not guaranteed.</p>
      </section>

      <section className="jw-landing-section jw-about" aria-labelledby="jw-about-title">
        <p className="jw-landing-eyebrow">MEET 9JAWIN</p><h2 id="jw-about-title">What is 9jaWin?</h2>
        <p>9jaWin is Nigeria’s real-money Whot platform. Play head-to-head in 2-player matches or join 4-player tables, with stake tiers and match rules shown before you play. We aim to provide fair matches, with wallet deposits and withdrawals available to eligible users. Results depend on gameplay—there are no guaranteed winnings.</p>
        <div className="jw-landing-feature-row"><span>♧ <b>2-player & 4-player</b></span><span>◈ <b>Fair-play focus</b></span><span>₦ <b>Wallet controls</b></span></div>
      </section>

      <section className="jw-landing-section" aria-labelledby="jw-how-title">
        <p className="jw-landing-eyebrow">FOUR SIMPLE STEPS</p><h2 id="jw-how-title">How it works</h2>
        <ol className="jw-steps">
          <li><span>01</span><div><h3>Create your account</h3><p>Sign up to get started.</p></div></li>
          <li><span>02</span><div><h3>Fund your wallet</h3><p>Add funds using the available wallet options.</p></div></li>
          <li><span>03</span><div><h3>Pick a stake and play Whot</h3><p>Review the entry stake and prize pool before joining a match.</p></div></li>
          <li><span>04</span><div><h3>Withdraw eligible winnings</h3><p>Request a withdrawal from your available wallet balance, subject to platform rules.</p></div></li>
        </ol>
      </section>

      <LobbyPreview />

      <section className="jw-landing-section" aria-labelledby="jw-gallery-title">
        <p className="jw-landing-eyebrow">SEE THE GAME</p><h2 id="jw-gallery-title">Inside a Whot match</h2>
        <p className="jw-landing-muted">Real staging gameplay screenshots will appear here. No player account or wallet information is displayed.</p>
        <div className="jw-gallery-grid">{GALLERY.map(item => <GalleryCard key={item.src} item={item} />)}</div>
      </section>

      <footer className="jw-landing-footer">
        <Link to="/" className="jw-landing-brand"><span>9ja</span><strong>Win</strong></Link>
        <p className="jw-landing-responsible">18+ only. Play responsibly. You can lose the money you stake. Winnings are not guaranteed.</p>
        <nav aria-label="Footer"><Link to="/support">Support</Link></nav>
        <small>© {new Date().getFullYear()} 9jaWin</small>
      </footer>
    </main>
  );
}
